import { useEffect, useMemo, useRef, useState } from "react";
import { BACA_LEVELS, bacaClips, bacaLevelClips, tadikaGameClips, type BacaClip } from "@jomcerdik/shared";
import { api, errorMessage } from "../../lib/api";
import { Alert, Button } from "../../components/Form";

/* Owner-only screen (BM). Record a real Malay voice for each Tadika clip; it replaces generated audio. */

type AdminAudioState = { gen: { provider: string; generatedAt: string; clips: Record<string, string> }; human: Record<string, { file: string; type: string; at: string }> };
const KIND: Record<BacaClip["kind"], string> = { syl: "Suku kata", word: "Perkataan", drill: "Eja + sebut", line: "Ayat", jawi: "Huruf Jawi" };

/** Minimal WAV recorder (16-bit mono) — plays on every phone and browser, no extra library. */
function useWavRecorder() {
  const ctx = useRef<{ ac: AudioContext; src: MediaStreamAudioSourceNode; node: ScriptProcessorNode; stream: MediaStream; chunks: Float32Array[] } | null>(null);
  const [recording, setRecording] = useState(false);
  async function start() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } });
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ac = new AC();
    const src = ac.createMediaStreamSource(stream);
    const node = ac.createScriptProcessor(4096, 1, 1);
    const chunks: Float32Array[] = [];
    node.onaudioprocess = (e) => chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
    src.connect(node); node.connect(ac.destination);
    ctx.current = { ac, src, node, stream, chunks };
    setRecording(true);
  }
  async function stop(): Promise<Blob | null> {
    const c = ctx.current;
    setRecording(false);
    if (!c) return null;
    c.node.disconnect(); c.src.disconnect(); c.stream.getTracks().forEach((t) => t.stop());
    const rate = c.ac.sampleRate;
    await c.ac.close();
    ctx.current = null;
    const all = new Float32Array(c.chunks.reduce((n, x) => n + x.length, 0));
    let o = 0; for (const x of c.chunks) { all.set(x, o); o += x.length; }
    return encodeWav(trim(all, rate), rate, 22050);
  }
  return { recording, start, stop };
}

/** Cut leading/trailing silence, keeping 120 ms around the voice. */
function trim(x: Float32Array, rate: number): Float32Array {
  const thr = 0.02; const pad = Math.round(rate * 0.12);
  let a = 0; while (a < x.length && Math.abs(x[a]!) < thr) a++;
  let b = x.length - 1; while (b > a && Math.abs(x[b]!) < thr) b--;
  return x.subarray(Math.max(0, a - pad), Math.min(x.length, b + pad));
}

function encodeWav(x: Float32Array, from: number, to: number): Blob {
  const n = Math.floor((x.length * to) / from);
  const data = new DataView(new ArrayBuffer(44 + n * 2));
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) data.setUint8(o + i, s.charCodeAt(i)); };
  let peak = 0; for (const v of x) peak = Math.max(peak, Math.abs(v));
  const gain = peak > 0 ? Math.min(4, 0.9 / peak) : 1; // gentle normalise
  str(0, "RIFF"); data.setUint32(4, 36 + n * 2, true); str(8, "WAVE"); str(12, "fmt ");
  data.setUint32(16, 16, true); data.setUint16(20, 1, true); data.setUint16(22, 1, true);
  data.setUint32(24, to, true); data.setUint32(28, to * 2, true); data.setUint16(32, 2, true); data.setUint16(34, 16, true);
  str(36, "data"); data.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const v = Math.max(-1, Math.min(1, (x[Math.floor((i * from) / to)] ?? 0) * gain));
    data.setInt16(44 + i * 2, v < 0 ? v * 0x8000 : v * 0x7fff, true);
  }
  return new Blob([data.buffer], { type: "audio/wav" });
}

export function AudioPanel() {
  const [state, setState] = useState<AdminAudioState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState(1);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const load = () => api<AdminAudioState>("/api/admin/audio").then(setState).catch((e) => setError(errorMessage(e)));
  useEffect(() => { void load(); }, []);

  const clips = useMemo(() => {
    if (level === 0) return tadikaGameClips();
    const seen = new Set<string>();
    for (const l of BACA_LEVELS) if (l.level < level) for (const c of bacaLevelClips(l)) seen.add(c.key);
    return bacaLevelClips(BACA_LEVELS.find((l) => l.level === level)!).filter((c) => !seen.has(c.key));
  }, [level]);
  const total = useMemo(() => bacaClips().length, []);
  const humanCount = state ? Object.keys(state.human).length : 0;
  const shown = clips.filter((c) => !onlyMissing || !state?.human[c.key]);

  return (
    <div className="space-y-4">
      <div className="rounded-card border border-surface-line bg-card p-4 text-sm">
        <p className="font-semibold">🎙️ Audio sebutan Tadika</p>
        <p className="text-muted">
          Rakaman manusia: <b>{humanCount}</b> / {total} klip · Audio jana: <b>{state ? Object.keys(state.gen.clips).length : "…"}</b> ({state?.gen.provider || "tiada"}).
          Rakaman manusia sentiasa diguna dahulu, kemudian audio jana. Huruf Jawi tanpa rakaman kekal senyap sehingga audio tersedia.
        </p>
        <p className="mt-1 text-xs text-muted">Tips: bilik senyap, telefon 20 cm dari mulut, sebut jelas dan perlahan. Untuk “Eja + sebut”: sebut setiap suku kata, berhenti sekejap, kemudian perkataan penuh.</p>
      </div>
      {error && <Alert kind="error">{error}</Alert>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" aria-pressed={level === 0} onClick={() => setLevel(0)} className="rounded-full border border-surface-line px-3 py-1 text-xs font-semibold">Permainan & Jawi</button>
        {BACA_LEVELS.map((l) => (
          <button key={l.level} type="button" aria-pressed={level === l.level} onClick={() => setLevel(l.level)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${level === l.level ? "bg-brand text-white" : "border border-surface-line"}`}>Buku {l.level}</button>
        ))}
        <label className="ml-auto flex items-center gap-1 text-xs"><input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} /> Belum dirakam sahaja</label>
      </div>
      <ul className="divide-y divide-surface-line rounded-card border border-surface-line">
        {shown.map((c) => <ClipRow key={c.key} c={c} state={state} onChange={load} />)}
        {shown.length === 0 && <li className="p-3 text-sm text-muted">Semua klip dalam buku ini sudah dirakam. 👍</li>}
      </ul>
    </div>
  );
}

function ClipRow({ c, state, onChange }: { c: BacaClip; state: AdminAudioState | null; onChange: () => void }) {
  const rec = useWavRecorder();
  const [take, setTake] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const human = state?.human[c.key];
  const gen = state?.gen.clips[c.key];
  const current = human?.file ?? gen;
  const play = (src: string) => { void new Audio(src).play().catch(() => setMsg("Tidak dapat dimainkan.")); };

  async function toggle() {
    setMsg(null);
    try {
      if (rec.recording) setTake(await rec.stop());
      else { setTake(null); await rec.start(); }
    } catch { setMsg("Mikrofon tidak dapat dibuka. Benarkan akses mikrofon untuk laman ini."); }
  }
  async function save() {
    if (!take) return;
    setBusy(true);
    try {
      await api(`/api/admin/audio/human/${c.key}`, { method: "POST", body: take, headers: { "Content-Type": "audio/wav" } });
      setTake(null); setMsg("Disimpan."); onChange();
    } catch (e) { setMsg(errorMessage(e)); } finally { setBusy(false); }
  }
  async function remove() {
    if (!confirm(`Buang rakaman untuk "${c.text}"?`)) return;
    setBusy(true);
    try { await api(`/api/admin/audio/human/${c.key}`, { method: "DELETE" }); onChange(); } catch (e) { setMsg(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <li className="flex flex-wrap items-center gap-2 p-3 text-sm">
      <div className="min-w-0 flex-1">
        <p className="text-lg font-bold">{c.letterId && <span lang="ar" dir="rtl">{c.letterId} · </span>}{c.text}</p>
        <p className="text-xs text-muted">{KIND[c.kind]} · sebutan: <span className="font-mono">{c.pron}</span> · IPA: <span className="font-mono">/{c.ipa}/</span></p>
        <p className="text-xs">{human ? <span className="font-semibold text-emerald-700">✅ Rakaman manusia</span> : gen ? <span className="text-amber-700">🤖 Audio jana ({state?.gen.provider}) — belum disahkan</span> : <span className="text-muted">— tiada audio</span>}</p>
        {msg && <p className="text-xs text-muted">{msg}</p>}
      </div>
      {current && <Button variant="secondary" onClick={() => play(`/api/learn/audio/f/${current}`)}>▶ Dengar</Button>}
      <Button variant={rec.recording ? "danger" : "primary"} onClick={toggle}>{rec.recording ? "⏹ Berhenti" : "🎙 Rakam"}</Button>
      {take && <>
        <Button variant="secondary" onClick={() => play(URL.createObjectURL(take))}>▶ Semak</Button>
        <Button onClick={save} busy={busy}>Simpan</Button>
      </>}
      {human && <Button variant="secondary" onClick={remove} busy={busy}>🗑</Button>}
    </li>
  );
}

